import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { BoardDoc } from '../../doc/BoardDoc';
import { PaneMenu } from '../../ui/PaneMenu';
import { IconClose, IconDots, IconFolder, IconPlus } from '../../ui/icons';
import { viewCenter } from '../actions';
import { useAssetUrl } from '../assets/localAssets';
import { useBoard, usePane, usePaneStore, useReadOnly, useYMap } from '../context';
import { flash } from '../flash';
import type { PaneStore } from '../paneStore';
import { setActivePane } from '../useKeyboard';
import { addToDrawer, placeCutout } from './drawerActions';
import {
  addFolder,
  cutoutsOf,
  deleteCutout,
  deleteFolder,
  foldersOf,
  moveCutout,
  renameCutout,
  renameFolder,
  useDrawer,
  type DrawerCutout,
  type DrawerFolder,
} from './drawerDoc';
import { beginTileDrag, DRAWER_DROP_EVENT, DRAWER_HOVER_EVENT, FOLDER_ATTR, type DrawerDropDetail, type TileDrop } from './drawerDrag';

/** Which folder the drawer shows: every cutout, the unsorted ones (''), or a folder id. */
type FolderKey = 'all' | '' | string;

const FOLDER_KEY = 'vds:drawer-folder';

const folderAttr = (key: string) => ({ [FOLDER_ATTR]: key });

/**
 * The cutout drawer: a panel along the pane's right edge (its bottom edge on phones) holding
 * saved cutouts, shared by all of the user's boards. Selected items go in with the Add button,
 * Shift+Q, or by dragging them onto the panel (onto a folder to file them at once); a tile
 * dragged onto a canvas places a copy there, a tile dragged onto a folder is filed in it.
 */
export function CutoutDrawer({ onClose }: { onClose: () => void }) {
  const { drawer, missing } = useDrawer(true);
  return (
    <aside className="drawer" onPointerDown={(e) => e.stopPropagation()}>
      <div className="insp-head drawer-head">
        <span>Cutout drawer</span>
        <button type="button" className="panel-close" aria-label="Close the drawer" title="Close the drawer (Q)" onClick={onClose}>
          <IconClose />
        </button>
      </div>
      {drawer ? (
        <DrawerBody drawer={drawer} />
      ) : (
        <div className="drawer-note">{missing ? 'The drawer could not be opened. It needs a connection the first time it is used on a device.' : 'Opening…'}</div>
      )}
    </aside>
  );
}

const noSync = () => () => {};

function DrawerBody({ drawer }: { drawer: BoardDoc }) {
  const board = useBoard();
  const pane = usePaneStore();
  const readOnly = useReadOnly();
  const hasSelection = usePane((s) => s.selection.size > 0);
  const folders = useYMap<DrawerFolder>(foldersOf(drawer));
  const cutouts = useYMap<DrawerCutout>(cutoutsOf(drawer));
  const provider = drawer.provider;
  const sync = useSyncExternalStore(provider ? provider.store.subscribe : noSync, () => (provider ? provider.store.getState().status : 'synced'));
  const rootRef = useRef<HTMLDivElement>(null);

  const [active, setActiveState] = useState<FolderKey>(() => {
    try {
      return localStorage.getItem(FOLDER_KEY) ?? 'all';
    } catch {
      return 'all';
    }
  });
  const setActive = (key: FolderKey) => {
    setActiveState(key);
    try {
      localStorage.setItem(FOLDER_KEY, key);
    } catch {
      /* private mode */
    }
  };
  // a folder deleted meanwhile (here or on another device) falls back to everything
  const shownKey: FolderKey = active === 'all' || active === '' || folders[active] ? active : 'all';

  const [renaming, setRenaming] = useState<{ kind: 'folder' | 'cutout'; id: string } | null>(null);
  const [menu, setMenu] = useState<{ kind: 'folder' | 'cutout'; id: string; anchor: HTMLElement } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const closeMenu = useCallback(() => {
    setMenu(null);
    setConfirmDelete(false);
  }, []);
  /** Folder a dragged tile or dragged board items hover over; `whole`: board items over the panel. */
  const [hover, setHover] = useState<{ key: string | null; whole: boolean }>({ key: null, whole: false });
  const [saving, setSaving] = useState(0);

  const folderRows = useMemo(() => Object.entries(folders).sort((a, b) => a[1].order - b[1].order || a[1].name.localeCompare(b[1].name)), [folders]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of Object.values(cutouts)) {
      const key = c.folderId && folders[c.folderId] ? c.folderId : '';
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [cutouts, folders]);
  const tiles = useMemo(
    () =>
      Object.entries(cutouts)
        .filter(([, c]) => shownKey === 'all' || (c.folderId && folders[c.folderId] ? c.folderId : '') === shownKey)
        .sort((a, b) => b[1].createdAt - a[1].createdAt),
    [cutouts, folders, shownKey],
  );
  const total = Object.keys(cutouts).length;

  /** Saves board items as a cutout; `key` is the folder they were dropped on (null: the one shown). */
  const save = useCallback(
    async (from: { board: BoardDoc; pane: PaneStore }, ids: string[], key: string | null) => {
      const target = key ?? (shownKey === 'all' ? '' : shownKey);
      setSaving((n) => n + 1);
      const r = await addToDrawer(from, ids, target === '' ? null : target);
      setSaving((n) => n - 1);
      const el = rootRef.current;
      if (!el) return;
      if (r.ok) flash(el, target && folders[target] ? `Saved to ${folders[target].name}` : 'Saved to the drawer');
      else if (r.reason !== 'empty') flash(el, 'Could not save the cutout');
    },
    [folders, shownKey],
  );

  // Shift+Q: the keyboard asked for the selection to be saved (it opened the drawer if need be)
  const saveRequested = usePane((s) => s.drawerSaveRequested);
  useEffect(() => {
    // the store is what counts: taking the request clears it, so an effect run twice saves once
    if (!saveRequested || !pane.getState().drawerSaveRequested) return;
    pane.getState().requestDrawerSave(false);
    void save({ board, pane }, [...pane.getState().selection], null);
  }, [saveRequested, save, board, pane]);

  // board items dragged onto the panel (see useGestures): highlight while they hover, save on release
  useEffect(() => {
    const el = rootRef.current?.closest<HTMLElement>('.drawer');
    if (!el) return;
    const onHover = (e: Event) => {
      const d = (e as CustomEvent<{ folderKey: string | null } | null>).detail;
      setHover((h) => {
        const next = d ? { key: d.folderKey, whole: true } : { key: null, whole: false };
        return h.key === next.key && h.whole === next.whole ? h : next;
      });
    };
    const onDrop = (e: Event) => {
      const d = (e as CustomEvent<DrawerDropDetail>).detail;
      setHover({ key: null, whole: false });
      void save({ board: d.board, pane: d.pane }, d.ids, d.folderKey).finally(() => d.after?.());
    };
    el.addEventListener(DRAWER_HOVER_EVENT, onHover);
    el.addEventListener(DRAWER_DROP_EVENT, onDrop);
    return () => {
      el.removeEventListener(DRAWER_HOVER_EVENT, onHover);
      el.removeEventListener(DRAWER_DROP_EVENT, onDrop);
    };
  }, [save]);

  const onTileDrop = (id: string, drop: TileDrop) => {
    if (drop.kind === 'folder') {
      moveCutout(drawer, id, drop.folderKey === '' ? null : drop.folderKey);
      return;
    }
    if (drop.board.provider?.store.getState().role === 'viewer') {
      flash(drop.el, 'This board is view-only');
      return;
    }
    setActivePane(drop.pane);
    void placeCutout({ board: drop.board, pane: drop.pane }, drawer, id, drop.world);
  };

  const placeInView = (id: string) => {
    if (readOnly) {
      if (rootRef.current) flash(rootRef.current, 'This board is view-only');
      return;
    }
    void placeCutout({ board, pane }, drawer, id, viewCenter(pane));
  };

  const newFolder = () => {
    const id = addFolder(drawer, 'New folder');
    setActive(id);
    setRenaming({ kind: 'folder', id });
  };

  const chip = (key: FolderKey, label: string, count: number, folderId?: string) => {
    const droppable = key !== 'all';
    const isRenaming = folderId !== undefined && renaming?.kind === 'folder' && renaming.id === folderId;
    return (
      <span
        key={key || '__unsorted'}
        className={`drawer-chip${shownKey === key ? ' is-active' : ''}${droppable && hover.key === key ? ' is-drop' : ''}`}
        {...(droppable ? folderAttr(key) : {})}
      >
        {isRenaming ? (
          <NameInput
            value={label}
            onCommit={(name) => {
              if (name) renameFolder(drawer, folderId, name);
              setRenaming(null);
            }}
          />
        ) : (
          <button type="button" className="drawer-chip-btn" onClick={() => setActive(key)} onDoubleClick={folderId ? () => setRenaming({ kind: 'folder', id: folderId }) : undefined}>
            {folderId && <IconFolder />}
            <span className="drawer-chip-name">{label}</span>
            <span className="drawer-chip-count">{count}</span>
          </button>
        )}
        {folderId && shownKey === key && !isRenaming && (
          <button
            type="button"
            className="drawer-chip-more"
            aria-label={`Options for ${label}`}
            onClick={(e) => (menu?.id === folderId ? closeMenu() : setMenu({ kind: 'folder', id: folderId, anchor: e.currentTarget }))}
          >
            <IconDots />
          </button>
        )}
      </span>
    );
  };

  const menuCutout = menu?.kind === 'cutout' ? cutouts[menu.id] : undefined;
  const menuFolder = menu?.kind === 'folder' ? folders[menu.id] : undefined;
  const act = (fn: () => void) => () => {
    closeMenu();
    fn();
  };

  return (
    <div ref={rootRef} className={`drawer-body${hover.whole ? ' is-drop' : ''}`}>
      <div className="drawer-folders">
        {chip('all', 'All', total)}
        {chip('', 'Unsorted', counts.get('') ?? 0)}
        {folderRows.map(([id, f]) => chip(id, f.name, counts.get(id) ?? 0, id))}
        <button type="button" className="drawer-chip drawer-chip-add" title="New folder" aria-label="New folder" onClick={newFolder}>
          <IconPlus />
        </button>
      </div>
      <div className="drawer-bar">
        <button
          type="button"
          className="btn"
          disabled={!hasSelection}
          title="Save the selected items as a cutout (Shift+Q). Dragging them onto this panel does the same."
          onClick={() => void save({ board, pane }, [...pane.getState().selection], null)}
        >
          <IconPlus /> Add selection
        </button>
        {sync !== 'synced' && sync !== 'syncing' && (
          <span className="drawer-sync" title={sync === 'connecting' ? 'Fetching the drawer from the server' : 'Changes are kept on this device and sent when the server is back'}>
            {sync === 'connecting' ? 'loading…' : 'offline'}
          </span>
        )}
      </div>
      <div className="drawer-grid" {...(shownKey === 'all' ? {} : folderAttr(shownKey))}>
        {saving > 0 && (
          <div className="drawer-tile is-saving" aria-live="polite">
            <div className="drawer-thumb">saving…</div>
          </div>
        )}
        {tiles.map(([id, c]) => (
          <Tile
            key={id}
            cutout={c}
            renaming={renaming?.kind === 'cutout' && renaming.id === id}
            menuOpen={menu?.id === id}
            onRename={(name) => {
              if (name) renameCutout(drawer, id, name);
              setRenaming(null);
            }}
            onHover={(key) => setHover({ key, whole: false })}
            onDrop={(drop) => onTileDrop(id, drop)}
            onPlace={() => placeInView(id)}
            onMenu={(anchor) => (menu?.id === id ? closeMenu() : setMenu({ kind: 'cutout', id, anchor }))}
          />
        ))}
        {!tiles.length && !saving && (
          <div className="drawer-note">
            {total
              ? 'Nothing in this folder yet. Drag cutouts onto its name to file them here.'
              : 'Nothing saved yet. Select something on a board and press Add selection, or drag it onto this panel. Cutouts kept here can be dragged onto any of your boards.'}
          </div>
        )}
      </div>
      {!drawer.remote && <div className="drawer-foot">Kept in this browser only (no account).</div>}

      {menu && menuCutout && (
        <PaneMenu anchor={menu.anchor} placement="below" className="drawer-menu" onRequestClose={closeMenu}>
          <button type="button" role="menuitem" onClick={act(() => placeInView(menu.id))}>
            <span>Place in the view</span>
          </button>
          <button type="button" role="menuitem" onClick={act(() => setRenaming({ kind: 'cutout', id: menu.id }))}>
            <span>Rename</span>
          </button>
          <div className="sep" />
          {menuCutout.folderId && folders[menuCutout.folderId] && (
            <button type="button" role="menuitem" onClick={act(() => moveCutout(drawer, menu.id, null))}>
              <span>Move to Unsorted</span>
            </button>
          )}
          {folderRows
            .filter(([fid]) => fid !== menuCutout.folderId)
            .map(([fid, f]) => (
              <button key={fid} type="button" role="menuitem" onClick={act(() => moveCutout(drawer, menu.id, fid))}>
                <span>Move to {f.name}</span>
              </button>
            ))}
          {folderRows.length > 0 && <div className="sep" />}
          <button type="button" role="menuitem" className="danger" onClick={confirmDelete ? act(() => deleteCutout(drawer, menu.id)) : () => setConfirmDelete(true)}>
            <span>{confirmDelete ? 'Click again to delete' : 'Delete'}</span>
          </button>
        </PaneMenu>
      )}
      {menu && menuFolder && (
        <PaneMenu anchor={menu.anchor} placement="below" className="drawer-menu" onRequestClose={closeMenu}>
          <button type="button" role="menuitem" onClick={act(() => setRenaming({ kind: 'folder', id: menu.id }))}>
            <span>Rename</span>
          </button>
          <div className="sep" />
          <button
            type="button"
            role="menuitem"
            className="danger"
            title="The cutouts in it stay, unsorted"
            onClick={act(() => {
              deleteFolder(drawer, menu.id);
              setActive('all');
            })}
          >
            <span>Delete folder (keeps its cutouts)</span>
          </button>
        </PaneMenu>
      )}
    </div>
  );
}

function Tile({
  cutout,
  renaming,
  menuOpen,
  onRename,
  onHover,
  onDrop,
  onPlace,
  onMenu,
}: {
  cutout: DrawerCutout;
  renaming: boolean;
  menuOpen: boolean;
  onRename: (name: string) => void;
  onHover: (folderKey: string | null) => void;
  onDrop: (drop: TileDrop) => void;
  onPlace: () => void;
  onMenu: (anchor: HTMLElement) => void;
}) {
  const moreRef = useRef<HTMLButtonElement>(null);
  const size = `${cutout.w} × ${cutout.h}${cutout.count > 1 ? ` · ${cutout.count} items` : ''}`;
  return (
    <div
      className={`drawer-tile${menuOpen ? ' is-menu' : ''}`}
      title={`${cutout.name} (${size})\nDrag onto a board, or double-click to place it in the view`}
      onPointerDown={(e) =>
        beginTileDrag(e, {
          preview: e.currentTarget.querySelector('img')?.src,
          w: cutout.w,
          h: cutout.h,
          onHover,
          onDrop,
        })
      }
      onDoubleClick={(e) => {
        if (!(e.target as Element).closest('button, input')) onPlace();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        // a finger held still raises this too, but that is the start of a drag
        if ((e.nativeEvent as PointerEvent).pointerType !== 'touch' && moreRef.current) onMenu(moreRef.current);
      }}
    >
      <div className="drawer-thumb">{cutout.thumbAssetId ? <Thumb assetId={cutout.thumbAssetId} /> : <span>{cutout.kind}</span>}</div>
      {renaming ? <NameInput value={cutout.name} onCommit={onRename} /> : <div className="drawer-name">{cutout.name}</div>}
      <button ref={moreRef} type="button" className="drawer-more" aria-label={`Options for ${cutout.name}`} onClick={(e) => onMenu(e.currentTarget)}>
        <IconDots />
      </button>
    </div>
  );
}

function Thumb({ assetId }: { assetId: string }) {
  const url = useAssetUrl(assetId, 'original');
  return url ? <img src={url} alt="" draggable={false} decoding="async" /> : null;
}

/** Inline rename: Enter or leaving the field commits, Escape keeps the old name. */
function NameInput({ value, onCommit }: { value: string; onCommit: (name: string) => void }) {
  const done = useRef(false);
  const commit = (name: string) => {
    if (done.current) return;
    done.current = true;
    onCommit(name.trim().slice(0, 80));
  };
  return (
    <input
      className="drawer-input"
      defaultValue={value}
      autoFocus
      maxLength={80}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => commit(e.currentTarget.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') commit(e.currentTarget.value);
        else if (e.key === 'Escape') commit('');
      }}
    />
  );
}
